import { useEffect, useEffectEvent, useState } from 'react';
import { useNavigate } from 'react-router';
import { WorkbenchField, WorkbenchNotice } from '@ndrc/ui';
import { useDesk } from '../desk/context';
import { t } from '../messages';
import {
  dateLabel,
  editable,
  remainingSeconds,
  sameDraft,
  sourceKey,
  timeLabel,
  type Attempt,
  type Draft,
  type LocalDraft,
} from './model';
import { readSession, saveSession, usePracticeRequest } from './usePracticeRequest';
import { SourcePicker } from './SourcePicker';
export function PracticeEditor({
  classId,
  attempt,
  cms,
  readOnly,
  refresh,
}: {
  classId: string;
  attempt: Attempt;
  cms: boolean;
  readOnly: boolean;
  refresh: () => Promise<void>;
}) {
  const { me } = useDesk(),
    navigate = useNavigate();
  const scope = `ndrc-practice:${me.id}:${classId}:${attempt.assignmentId}:draft`;
  const [local, setLocal] = useState<LocalDraft>(() => {
    const saved = readSession<LocalDraft | null>(scope, null);
    return saved &&
      Number.isInteger(saved.revision) &&
      typeof saved.value?.response === 'string' &&
      Array.isArray(saved.value.sources)
      ? saved
      : { revision: attempt.revision, value: editable(attempt.draft) };
  });
  const [stored, setStored] = useState(true),
    [notice, setNotice] = useState('');
  const [seconds, setSeconds] = useState(() =>
    remainingSeconds(attempt.deadlineAt, attempt.serverNow, 0),
  );
  const request = usePracticeRequest(`${scope}:request`);
  useEffect(() => {
    const start = performance.now();
    const interval = window.setInterval(
      () =>
        setSeconds(
          remainingSeconds(attempt.deadlineAt, attempt.serverNow, performance.now() - start),
        ),
      1000,
    );
    return () => window.clearInterval(interval);
  }, [attempt.deadlineAt, attempt.serverNow]);
  const serverDraft = editable(attempt.draft);
  const dirty = !sameDraft(local.value, serverDraft);
  const conflict = local.revision !== attempt.revision && dirty;
  const closed = Boolean(attempt.submittedAt) || attempt.expired || seconds === 0;
  const frozen = readOnly || request.busy || Boolean(request.pending);
  function change(value: Draft, revision = local.revision) {
    const next = { revision, value };
    setLocal(next);
    setStored(saveSession(scope, next));
    setNotice('');
  }
  async function save() {
    if (frozen || closed || conflict) return;
    const result = await request.execute<Attempt>('practice.save', {
      classId,
      assignmentId: attempt.assignmentId,
      revision: attempt.revision,
      ...local.value,
    });
    if (result) {
      change(editable(result.draft), result.revision);
      setNotice(t('practice.saved'));
      await refresh();
    } else await refresh();
  }
  async function capture(final: boolean) {
    if (frozen || dirty || (!final && closed) || attempt.submittedAt) return;
    const result = await request.execute('practice.capture', {
      classId,
      assignmentId: attempt.assignmentId,
      revision: attempt.revision,
      final,
    });
    if (result !== undefined) {
      setNotice(t(final ? 'practice.finalSaved' : 'practice.versionSaved'));
      await refresh();
    }
  }
  async function retry() {
    if (readOnly || request.busy || !request.pending) return;
    const op = request.pending.op;
    const result = await request.execute<Attempt | { id: string }>(op, request.pending.body);
    if (result !== undefined) {
      if (op === 'practice.save' && 'draft' in result)
        change(editable(result.draft), result.revision);
      setNotice(t('practice.requestResolved'));
      await refresh();
    } else await refresh();
  }
  // The dependency array deliberately tracks the full command state: edits debounce, uncertain
  // requests and concurrent revisions suspend autosave until an explicit user decision.
  const autoSave = useEffectEvent(async () => {
    await save();
  });
  useEffect(() => {
    if (!dirty || frozen || closed || conflict) return;
    const timer = window.setTimeout(() => {
      void autoSave();
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [dirty, frozen, closed, conflict, local, attempt.revision]);
  useEffect(() => {
    if (!dirty && !request.pending) return;
    const protect = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [dirty, request.pending]);
  return (
    <div className="practice-card">
      <div className="practice-editor-head">
        <h3>{t('practice.response')}</h3>
        <div className={closed ? 'practice-timer practice-ended' : 'practice-timer'}>
          <span>{t(attempt.submittedAt ? 'practice.submitted' : 'practice.remaining')}</span>
          {!attempt.submittedAt && <strong>{timeLabel(seconds)}</strong>}
        </div>
      </div>
      <p>
        {attempt.savedAt
          ? t('practice.savedAt', { date: dateLabel(attempt.savedAt) })
          : t('practice.notSaved')}
      </p>
      {!stored || !request.stored ? (
        <WorkbenchNotice error>{t('practice.storageError')}</WorkbenchNotice>
      ) : null}
      {request.error && <WorkbenchNotice error>{request.error}</WorkbenchNotice>}
      {notice && <WorkbenchNotice>{notice}</WorkbenchNotice>}
      {request.pending && (
        <WorkbenchNotice>
          <p>{t('practice.pending')}</p>
          <button
            type="button"
            disabled={readOnly || request.busy}
            onClick={() => {
              void retry();
            }}
          >
            {t('practice.retry')}
          </button>
        </WorkbenchNotice>
      )}
      {(conflict || (closed && dirty)) && !request.pending && (
        <WorkbenchNotice error>
          <p>{t('practice.conflict')}</p>
          <details>
            <summary>{t('practice.serverVersion')}</summary>
            <p className="practice-prose">{attempt.draft.response}</p>
          </details>
          <div className="lab-actions">
            <button
              type="button"
              disabled={frozen}
              onClick={() => change(serverDraft, attempt.revision)}
            >
              {t('practice.useServer')}
            </button>
            <button
              type="button"
              disabled={frozen || closed}
              onClick={() => change(local.value, attempt.revision)}
            >
              {t('practice.keepLocal')}
            </button>
          </div>
        </WorkbenchNotice>
      )}
      {closed && (
        <WorkbenchNotice>
          {t(attempt.submittedAt ? 'practice.finalHint' : 'practice.expiredHint')}
        </WorkbenchNotice>
      )}
      <WorkbenchField label={t('practice.responseHint')}>
        <textarea
          rows={12}
          maxLength={60000}
          value={local.value.response}
          readOnly={frozen || closed}
          onChange={(event) => change({ ...local.value, response: event.target.value })}
        />
      </WorkbenchField>
      {cms && (
        <>
          <div className="lab-actions">
            <button
              type="button"
              onClick={() => {
                void navigate('/environments');
              }}
            >
              {t('practice.openCms')}
            </button>
            <span>{t('practice.sourcesHint')}</span>
          </div>
          <SourcePicker
            sources={local.value.sources}
            disabled={frozen || closed}
            onChange={(sources) => change({ ...local.value, sources })}
          />
          <ul className="practice-sources">
            {local.value.sources.map((source) => (
              <li key={sourceKey(source)}>
                <span>
                  {attempt.draft.sources.find((saved) => sourceKey(saved) === sourceKey(source))
                    ?.record.title ??
                    t('practice.sourceSelected', { kind: source.kind, id: source.externalId })}
                </span>
                <button
                  type="button"
                  disabled={frozen || closed}
                  onClick={() =>
                    change({
                      ...local.value,
                      sources: local.value.sources.filter(
                        (s) => sourceKey(s) !== sourceKey(source),
                      ),
                    })
                  }
                >
                  {t('practice.remove')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="lab-actions">
        <button
          type="button"
          className="lab-primary"
          disabled={frozen || closed || !dirty || conflict}
          onClick={() => {
            void save();
          }}
        >
          {request.busy ? t('practice.saving') : t('practice.save')}
        </button>
        <button
          type="button"
          disabled={frozen || closed || dirty}
          onClick={() => {
            void capture(false);
          }}
        >
          {t('practice.capture')}
        </button>
      </div>
      {!closed && (
        <p className="practice-muted">{t(dirty ? 'practice.unsaved' : 'practice.autoSaveHint')}</p>
      )}
      {!attempt.submittedAt && (
        <details>
          <summary>{t('practice.finish')}</summary>
          <p>{t('practice.finishHint')}</p>
          {dirty && <p>{t('practice.finishDirty')}</p>}
          <button
            type="button"
            disabled={frozen || dirty}
            onClick={() => {
              void capture(true);
            }}
          >
            {t('practice.confirmFinish')}
          </button>
        </details>
      )}
    </div>
  );
}
